//import cn from "classnames";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";

import { ProviderHooks } from "@/src/hook";
import { ComponentType, StrapiComponentType, DeepSkyType } from "@/src/type";
import { Log } from "@/src/util";

import { ButtonLinkModule, SectionModule } from "../component";

import { Header, Features, ImageGrid, Scheduler, Facts, About, Pricing, FurnishedDetails, MobileFooter } from "./component";

import styles from "./PropertyUnitDetails.module.scss";

interface PropertyUnitDetailsProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.PropertyUnitDetails> {}

function PropertyUnitDetails({
  section,
  slug,
  snapToGrid,
  lazy,
  searchParams,
  quality,
  mobileHint,
  deviceWidthHint,
  layout,
  calendarConfig,
}: PropertyUnitDetailsProps) {
  const { floorplans, externalRefId, externalRefType, unitCarousel } = ProviderHooks.useProperty();
  const propertyFloorplan = floorplans.find((opt) => opt.externalRefId === externalRefId && opt.externalRefType === externalRefType);
  const floorplan: DeepSkyType.AssetType.PropertyInventory.MarketingFloorPlan = propertyFloorplan?.floorplan;
  const propertyUnitDetails: StrapiComponentType.Section.PropertyUnitDetails = section.type?.[0];
  const elementIdToTriggerFooter = `property_unit_details_${propertyUnitDetails.id}`;

  useEffect(() => {
    Log.logToConsoleDebug(
      "view/DynamicPage/component/PropertyUnitDetails.tsx",
      "Property unit data",
      [{ propertyFloorplan }],
      Log.LOG_COLORS.STATE_CHANGE.BG_COLOR,
      Log.LOG_COLORS.STATE_CHANGE.COLOR
    );
  }, [propertyFloorplan]);

  if (!floorplan) {
    return <></>;
  }

  return (
    <SectionModule
      section={section}
      slug={slug}
      snapToGrid={snapToGrid}
      lazy={lazy}
      styleContainerId={propertyUnitDetails.id}
      searchParams={searchParams}
      mobileHint={mobileHint}
      deviceWidthHint={deviceWidthHint}
      quality={quality}
      stylePrefix="PropertyUnitDetails"
      layout={layout}
      classes={{
        section: styles.section,
      }}
    >
      <Header
        propertyUnitDetails={propertyUnitDetails}
        floorplan={floorplan}
        propertyFloorplan={propertyFloorplan}
      />
      <div
        className={styles.content}
        id={elementIdToTriggerFooter}
      >
        <ImageGrid
          title={floorplan.name}
          floorplan={floorplan}
          unitCarousel={unitCarousel}
          posterUrl={propertyFloorplan?.poster?.data?.attributes?.url}
          mobileHint={mobileHint}
          deviceWidthHint={deviceWidthHint}
          quality={quality}
        />
        <div className={styles.details}>
          <div className={styles.left}>
            <Facts
              propertyUnitDetails={propertyUnitDetails}
              floorplan={floorplan}
              propertyFloorplan={propertyFloorplan}
            />
            {propertyUnitDetails.scheduledTourCta && (
              <div className={styles.cta}>
                <ButtonLinkModule
                  buttonLink={propertyUnitDetails.scheduledTourCta}
                  className={styles.button}
                />
              </div>
            )}
            {propertyFloorplan?.furnishedAvailable && <FurnishedDetails propertyUnitDetails={propertyUnitDetails} />}
            <Pricing
              propertyUnitDetails={propertyUnitDetails}
              floorplan={floorplan}
            />
            <About
              titlePrefix={propertyUnitDetails.aboutTheTitle}
              title={propertyFloorplan?.title ?? floorplan.name}
              description={propertyFloorplan?.description ?? floorplan.description}
            />
            <Features
              propertyUnitDetails={propertyUnitDetails}
              propertyFloorplan={propertyFloorplan}
            />
            <MobileFooter
              elementIdToTriggerFooter={elementIdToTriggerFooter}
              floorplan={floorplan}
              propertyUnitDetails={propertyUnitDetails}
            />
          </div>
          <div className={styles.right}>
            <Scheduler
              propertyUnitDetails={propertyUnitDetails}
              calendarConfig={calendarConfig}
            />
          </div>
        </div>
        {propertyUnitDetails.pageDisclaimer && (
          <div className={styles.disclaimer}>
            <Typography
              variant="body1"
              className={styles.disclaimerText}
            >
              {propertyUnitDetails.pageDisclaimer}
            </Typography>
          </div>
        )}
      </div>
    </SectionModule>
  );
}

export default PropertyUnitDetails;
